import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Phone, Mail, MapPin, Plus, Pencil, Trash2, Building2, User as UserIcon } from "lucide-react";
import LeadCaptureDialog from "@/components/sales/LeadCaptureDialog";
import EditLeadDialog from "@/components/sales/EditLeadDialog";
import { isSalesManagerRole } from "@/lib/roleAliases";

export default function LeadsPage({ mode = "executive" }) {
  const { roleName, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [salesManagersById, setSalesManagersById] = useState({});
  const [branchesById, setBranchesById] = useState({});
  const [captureOpen, setCaptureOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadLookups = async () => {
    try {
      const [sm, br] = await Promise.all([
        api.get("/users/sales-managers"),
        api.get("/branches"),
      ]);
      setSalesManagersById(Object.fromEntries(sm.data.map((u) => [u.id, u])));
      setBranchesById(Object.fromEntries(br.data.map((b) => [b.id, b])));
    } catch (_e) { /* non-fatal for viewers */ }
  };

  useEffect(() => {
    load();
    loadLookups();
  }, []);

  const isExec = mode === "executive";
  const isManager = mode === "manager";
  const canEdit = isAdmin || roleName === "Marketing Manager" || isSalesManagerRole(roleName);
  const canDelete = isAdmin || roleName === "Marketing Manager";

  const doDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.client_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Marketing · {isExec ? "Executive" : "Manager"}</p>
          <h1 className="font-display text-3xl text-white mt-1">{isExec ? "Log a Lead" : "Team Leads"}</h1>
        </div>
        <button
          onClick={() => setCaptureOpen(true)}
          className="iz-btn-primary flex items-center gap-2"
          data-testid="capture-lead-button"
        >
          <Plus className="w-4 h-4" /> Capture Lead
        </button>
      </div>

      <div className="px-10 py-8">
        <div className="iz-card p-6">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#262626]">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">{isExec ? "Your captures" : "Team captures"}</p>
              <h2 className="font-display text-lg text-white">{leads.length} leads · handed to Sales pipeline</h2>
            </div>
          </div>
          <div className="space-y-2 max-h-[720px] overflow-y-auto">
            {leads.length === 0 && <p className="text-xs text-gray-500 text-center py-8">No leads logged yet.</p>}
            {leads.map(l => {
              const sm = l.assigned_sales_manager_id ? salesManagersById[l.assigned_sales_manager_id] : null;
              const branch = l.branch_id ? branchesById[l.branch_id] : null;
              return (
                <div key={l.id} className="p-4 rounded-md border border-[#262626] hover:border-[#d4af37]/40 transition-colors" data-testid={`lead-row-${l.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-white text-sm font-medium">{l.client_name}</p>
                        {l.project_name && (
                          <span className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]/80 border border-[#d4af37]/30 rounded px-1.5 py-0.5" data-testid={`lead-project-${l.id}`}>
                            {l.project_name}
                          </span>
                        )}
                        {branch && (
                          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5 flex items-center gap-1">
                            <Building2 className="w-2.5 h-2.5" /> {branch.name}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-400 font-mono">
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</span>
                        {l.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>}
                        {l.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.location}</span>}
                        {sm && (
                          <span className="flex items-center gap-1 text-[#d4af37]/90">
                            <UserIcon className="w-3 h-3" /> {sm.name}
                          </span>
                        )}
                      </div>
                      {l.remarks && <p className="text-xs text-gray-500 mt-2 italic">&ldquo;{l.remarks}&rdquo;</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                        {new Date(l.created_at).toLocaleDateString()}
                      </span>
                      {canEdit && isManager && (
                        <button
                          onClick={() => setEditingLead(l)}
                          className="p-1.5 rounded hover:bg-[#d4af37]/10 text-gray-500 hover:text-[#d4af37] transition-colors"
                          title="Edit lead"
                          data-testid={`edit-lead-${l.id}`}
                        >
                          <Pencil className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                      {canDelete && isManager && (
                        <button
                          onClick={() => doDelete(l)}
                          className="p-1.5 rounded hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors"
                          title="Delete lead"
                          data-testid={`delete-lead-${l.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <LeadCaptureDialog
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        onCreated={() => { load(); loadLookups(); }}
      />
      <EditLeadDialog
        open={!!editingLead}
        lead={editingLead}
        onClose={() => setEditingLead(null)}
        onSaved={() => { load(); loadLookups(); }}
      />
    </div>
  );
}
